import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getCargoOperationForCargoAndOperationType, getCargoOperationsAfter } from '../stowageRotationUtils';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createStowagePlanChange, createDeleteStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { getCargoStowedTanks, notStowedWeightInTanks, stowedWeightInTanks } from './stowageOperationUtils';
import Decimal from 'decimal.js';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import {
    findLoadingStowageItemsForCargo,
    findTransferredFromStowageItemsForCargo,
    findTransferredToStowageItemsForCargo,
    findUnloadingStowageItemsForCargo,
    findUnloadingStowageItemsForCargoOperation,
    getStowageItemsWeight,
} from './stowageItemUtils';
import { UUID } from 'crypto';

/**
 * Creates a new stowage item for a given tank, cargo operation and weight
 * @param tank The tank to create a stowage item for
 * @param cargoOperation The cargo operation associated with this stowage item
 * @param weight The weight of the stowage item
 * @returns A newly created Stowage item
 */
const createStowageItem = (tank: Tank, cargoOperation: RotationCargoOperation, weight: Decimal): Stowage => ({
    // TODO: CRYPTO-IMPORT - crypto.randomUUID() is used without importing crypto module. Should add 'import { randomUUID } from 'crypto';' or use a proper UUID generator.
    stowageGuid: crypto.randomUUID(),
    tankId: tank.tankId,
    cargoOperationGuid: cargoOperation.cargoOperationGuid,
    cargoOperation: cargoOperation,
    tank: tank,
    weight: weight,
});

/**
 * Collects all stowage items for a cargo in a tank, combining both loading and transfers
 * @param stowagePlan The stowage plan
 * @param tank The tank to check
 * @param cargoGuid The cargo GUID to filter by
 * @returns Combined stowage items array
 */
const collectStowageItems = (stowagePlan: StowagePlan, tank: Tank, cargoGuid: UUID) => {
    const loadingItems = findLoadingStowageItemsForCargo(stowagePlan, tank, cargoGuid);
    const unloadingItems = findUnloadingStowageItemsForCargo(stowagePlan, tank, cargoGuid);
    const transferToItems = findTransferredToStowageItemsForCargo(stowagePlan, tank, cargoGuid);
    const transferFromItems = findTransferredFromStowageItemsForCargo(stowagePlan, tank, cargoGuid);

    return {
        loadingItems: [...loadingItems, ...transferToItems],
        unloadingItems: [...unloadingItems, ...transferFromItems],
    };
};

/**
 * Updates or creates a new unloading stowage item based on existing items
 * @param stowagePlan The stowage plan to update
 * @param tank The tank for the operation
 * @param unloadingOperation The unloading operation
 * @param unloadingWeight The weight to unload (negative value)
 * @param availableWeight The available weight in the tank that can be unloaded
 * @returns The stowage item that was updated or created, or null if operation would exceed available weight
 */
const updateOrCreateUnloadingStowageItem = (
    stowagePlan: StowagePlan,
    tank: Tank,
    unloadingOperation: RotationCargoOperation,
    unloadingWeight: Decimal,
    availableWeight: Decimal
): Stowage | null => {
    const existingUnloadingItems = findUnloadingStowageItemsForCargoOperation(stowagePlan, tank, unloadingOperation.cargoOperationGuid);

    if (existingUnloadingItems.length > 0) {
        const existingItem = existingUnloadingItems[0];

        // Calculate what the total unloading would be after adding new weight
        const proposedTotalUnloading = existingItem.weight.plus(unloadingWeight);

        // Check if this would exceed available cargo (remember unloadingWeight is negative)
        // availableWeight is positive, proposedTotalUnloading is negative
        if (proposedTotalUnloading.abs().gt(availableWeight)) {
            return null;
        }

        existingItem.weight = proposedTotalUnloading;
        return existingItem;
    } else {
        // For new items, check if the unloading weight exceeds available
        if (unloadingWeight.abs().gt(availableWeight)) {
            return null;
        }

        const newStowageItem = createStowageItem(tank, unloadingOperation, unloadingWeight);
        stowagePlan.stowageItems.push(newStowageItem);
        return newStowageItem;
    }
};

/**
 * Creates a stowage plan change for a stowage item
 * @param stowagePlan The stowage plan
 * @param stowageItem The stowage item that was changed
 * @returns The stowage plan change
 */
const createChangeForStowageItem = (stowagePlan: StowagePlan, stowageItem: Stowage): StowagePlanChange => {
    const stowageChangeData = {
        tankId: stowageItem.tankId,
        cargoOperationGuid: stowageItem.cargoOperationGuid,
        weight: stowageItem.weight,
    };

    return createStowagePlanChange(stowagePlan.stowagePlanGuid, stowageItem.stowageGuid, StowageChangeDataType.StowageItem, stowageChangeData);
};

/**
 * Updates unstow operations for a specific cargo to ensure proper balance
 * between loading and unloading operations.
 * @param stowagePlan The stowage plan to update
 * @param cargoOperation The cargo operation that triggered the update
 * @returns Array of stowage plan changes made during the update
 */
export const updateUnstowOperationsForCargo = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): StowagePlanChange[] => {
    const affectedTanks: Tank[] = [];
    const loadingCargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargoOperation.cargo, 'Loading');
    const unloadingCargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargoOperation.cargo, 'Unloading');
    const stowagePlanChanges: StowagePlanChange[] = [];

    // Find unloading operations that haven't been fully unstowed yet
    const unstowedUnloadingCargoOperations = unloadingCargoOperations?.filter((c) =>
        notStowedWeightInTanks(stowagePlan, c).toDecimalPlaces(0, Decimal.ROUND_DOWN).abs().gt(0)
    );

    const totalLoaded = loadingCargoOperations
        .map((op) => stowedWeightInTanks(stowagePlan, op))
        .reduce((acc, val) => acc.plus(val), new Decimal(0));

    const totalUnloaded = unloadingCargoOperations
        .map((op) => stowedWeightInTanks(stowagePlan, op))
        .reduce((acc, val) => acc.plus(val), new Decimal(0));

    const totalDiffLoadedUnloaded = totalLoaded.minus(totalUnloaded.abs());

    // Get tanks where the cargo is stowed
    const tanksWithCargo = getCargoStowedTanks(stowagePlan, cargoOperation.cargo);

    // Check if we have the right conditions for auto-unstowing
    if (
        loadingCargoOperations?.length >= 1 &&
        unstowedUnloadingCargoOperations?.length === 1 &&
        unstowedUnloadingCargoOperations[0].cargoOperationGuid !== cargoOperation.cargoOperationGuid &&
        unstowedUnloadingCargoOperations[0].cargoGuid === cargoOperation.cargoGuid &&
        totalDiffLoadedUnloaded.gt(0)
    ) {
        const unloadingOperation = unstowedUnloadingCargoOperations[0];

        tanksWithCargo.forEach((tank) => {
            // Collect all stowage items for this cargo in this tank
            const { loadingItems, unloadingItems } = collectStowageItems(stowagePlan, tank, unloadingOperation.cargoGuid);

            if (loadingItems.length > 0) {
                // Calculate weights
                const loadedWeight = getStowageItemsWeight(loadingItems);
                const alreadyUnstowedWeight = getStowageItemsWeight(unloadingItems);
                const remainingWeight = loadedWeight.plus(alreadyUnstowedWeight);

                // Only proceed if there's actually weight remaining in the tank to unstow
                // Use a tolerance to account for floating-point precision
                const WEIGHT_TOLERANCE = 0.001;
                if (remainingWeight.gt(WEIGHT_TOLERANCE)) {
                    // Calculate the weight to unstow (negative value for unloading)
                    const unloadingWeight = new Decimal(Math.min(unloadingOperation.nominatedWeight, remainingWeight.toNumber())).times(-1);

                    // Update or create the unloading stowage item with validation
                    const unloadingStowageItem = updateOrCreateUnloadingStowageItem(
                        stowagePlan,
                        tank,
                        unloadingOperation,
                        unloadingWeight,
                        remainingWeight  // Pass available weight for validation
                    );

                    // Only create change if the operation was successful
                    if (unloadingStowageItem) {
                        // Create a stowage plan change for tracking
                        stowagePlanChanges.push(createChangeForStowageItem(stowagePlan, unloadingStowageItem));

                        // Mark this tank as affected
                        affectedTanks.push(tank);
                    }
                }
            }
        });
    }
    // Handle one-to-one loading/unloading scenario
    // Only apply this logic when there is exactly ONE unloading operation
    else if (
        totalDiffLoadedUnloaded.lt(0) &&
        loadingCargoOperations.length === 1 &&
        unloadingCargoOperations.length === 1 && // Only exactly 1 unloading operation
        loadingCargoOperations[0].cargoGuid === unloadingCargoOperations[0].cargoGuid &&
        loadingCargoOperations[0].operationType === 'Loading' &&
        unloadingCargoOperations[0].operationType === 'Unloading'
    ) {
        const loadingOp = loadingCargoOperations[0];
        const unloadingOp = unloadingCargoOperations[0];

        // Validate operations before proceeding
        if (!loadingOp?.cargoOperationGuid || !unloadingOp?.cargoOperationGuid) {
            return;
        }

        // Find all stowage items for the loading operation with validation
        const loadingStowageItems = stowagePlan.stowageItems.filter(
            (item) => item?.cargoOperationGuid === loadingOp.cargoOperationGuid &&
                item?.weight?.gt(0) && // Only positive weights for loading
                item?.tank?.tankId // Valid tank
        );

        if (loadingStowageItems.length === 0) {
            return;
        }

        // Track processed tanks
        const processedTanks = new Set<number>();

        loadingStowageItems.forEach((loadingItem) => {
            try {
                // Validate loading item
                if (!loadingItem?.weight || !loadingItem?.tank) {
                    return;
                }

                const unloadingWeight = loadingItem.weight.times(-1); // Negative for unloading
                const tank = loadingItem.tank;

                // Validate tank
                if (!tank.tankId || processedTanks.has(tank.tankId)) {
                    return;
                }

                const existingUnloadingItems = findUnloadingStowageItemsForCargoOperation(
                    stowagePlan,
                    tank,
                    unloadingOp.cargoOperationGuid
                );

                if (existingUnloadingItems && existingUnloadingItems.length > 0) {
                    const existingItem = existingUnloadingItems.find(
                        item =>
                            item.cargoOperation.cargoGuid === unloadingOp.cargoGuid &&
                            item.tank.tankId === tank.tankId &&
                            item.cargoOperation.operationType === 'Unloading'
                    );

                    // Validate existing item
                    if (!existingItem || !existingItem.weight) {
                        return;
                    }

                    // Calculate available weight in this tank for this cargo
                    const { loadingItems, unloadingItems } = collectStowageItems(stowagePlan, tank, unloadingOp.cargoGuid);
                    const loadedWeight = getStowageItemsWeight(loadingItems);
                    const alreadyUnloadedWeight = getStowageItemsWeight(unloadingItems);
                    const availableWeight = loadedWeight.plus(alreadyUnloadedWeight); // alreadyUnloadedWeight is negative

                    // Validate that the new unloading weight doesn't exceed available cargo
                    if (unloadingWeight.abs().gt(availableWeight)) {
                        return;
                    }

                    existingItem.weight = unloadingWeight;

                    stowagePlanChanges.push(createChangeForStowageItem(stowagePlan, existingItem));

                    // Track processed tank
                    processedTanks.add(tank.tankId);

                    // Only add tank if not already added
                    if (!affectedTanks.some(t => t.tankId === tank.tankId)) {
                        affectedTanks.push(tank);
                    }
                }
                // else {
                //     const newUnloadingItem = createStowageItem(tank, unloadingOp, unloadingWeight);

                //     if (newUnloadingItem) {
                //         stowagePlan.stowageItems.push(newUnloadingItem);
                //         stowagePlanChanges.push(createChangeForStowageItem(stowagePlan, newUnloadingItem));

                //         totalProcessedWeight = totalProcessedWeight.plus(unloadingWeight.abs());
                //         processedTanks.add(tank.tankId);

                //         if (!affectedTanks.some(t => t.tankId === tank.tankId)) {
                //             affectedTanks.push(tank);
                //         }
                //     } else {
                //         logUnstowMessage(`Failed to create new unloading item for tank ${tank.odfjellTankName}`);
                //     }
                // }

            } catch (error) {
                // Error processing loading item - silently continue
            }
        });

        // Final validation - weights are validated but not logged
    }

    return stowagePlanChanges;
};

/**
 * Removes stowage items for subsequent unloading operations in the same tanks
 * when an earlier unloading operation is deleted/undone.
 * 
 * Use case: If cargo is unloaded at Port B and Port C, and the user undoes
 * the unloading at Port B for specific tanks, then the unloading at Port C
 * for those same tanks should also be removed (cascade deletion).
 * 
 * @param stowagePlan The stowage plan to update
 * @param cargoOperation The unloading cargo operation being deleted
 * @param tanks The tanks where stowage is being undone
 * @returns Array of stowage plan changes for the deleted subsequent unloading items
 */
export const removeSubsequentUnloadingStowageItems = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    tanks: Tank[]
): StowagePlanChange[] => {
    // Only process unloading operations
    if (cargoOperation.operationType !== 'Unloading') {
        return [];
    }

    const stowagePlanChanges: StowagePlanChange[] = [];
    const tankIds = tanks.map(t => t.tankId);

    // Step 1: Find all subsequent cargo operations (operations that occur after this one)
    const subsequentOperations = getCargoOperationsAfter(stowagePlan, cargoOperation);

    // Step 2: Filter to only unloading operations for the same cargo
    const subsequentUnloadingOperations = subsequentOperations.filter(
        op => op.operationType === 'Unloading' && op.cargoGuid === cargoOperation.cargoGuid
    );

    if (subsequentUnloadingOperations.length === 0) {
        return [];
    }

    // Step 3: For each subsequent unloading operation, find and delete stowage items in the same tanks
    subsequentUnloadingOperations.forEach(unloadingOp => {
        // Find stowage items for this operation in the affected tanks
        const stowageItemsToDelete = stowagePlan.stowageItems.filter(item =>
            item.cargoOperationGuid === unloadingOp.cargoOperationGuid &&
            tankIds.includes(item.tankId)
        );

        // Step 4: Delete the stowage items and create change records
        stowageItemsToDelete.forEach(item => {
            // Create delete change
            const deleteChange = createDeleteStowagePlanChange(
                stowagePlan.stowagePlanGuid,
                item.stowageGuid,
                StowageChangeDataType.StowageItem
            );
            stowagePlanChanges.push(deleteChange);
        });

        // Remove from stowage plan
        stowagePlan.stowageItems = stowagePlan.stowageItems.filter(item =>
            !stowageItemsToDelete.includes(item)
        );
    });

    return stowagePlanChanges;
};
